// front-end/src/components/news-manager/news-manager.js

import {apiDelete, apiGet, apiPatch, apiPost, apiPut} from '../../utils/api';
import {getAuthToken} from '../auth';
import {showErrorNotification, showSuccessNotification} from '../../utils/notifications';
import {openLinkPageModal} from '../diagnostic-manager/link-page-modal';
import {config} from '../../config/app-config';

export class NewsManager {
    constructor() {
        this.init();
    }

    init() {
        console.log('NewsManager initialized');
        document.addEventListener('blockPublished', this.handleBlockPublished.bind(this));
        document.addEventListener('blockAdded', this.handleBlockAdded.bind(this));
        this.processExistingNewsBlocks();
    }

    handleBlockAdded(event) {
        const { blockElement } = event.detail;
        const blockName = blockElement.getAttribute('data-block-name');
        if (blockName && blockName.startsWith('news-') && getAuthToken()) {
            this.addLinkButtonToBlock(blockElement);
        }
    }

    // НОВОЕ: Проверяем, должны ли показываться статусы
    shouldShowStatusControls() {
        return window.location.pathname.match(/news-feed(\.html)?$/);
    }

    handleBlockPublished(event) {
        const {blockElement, blockId, blockData} = event.detail;
        const blockName = blockElement.getAttribute('data-block-name');

        if (blockName && blockName.startsWith('news-')) {
            console.log('News block published:', blockName);
            this.activateNewsLink(blockElement, blockName);

            const pendingId = blockElement.getAttribute('data-pending-link-id');
            if (pendingId) {
                this._applyPendingLink(blockElement, blockId, pendingId);
            } else {
                this.createNewsEntity(blockElement, blockId, blockData);
            }
        }
    }

    async _applyPendingLink(blockElement, blockId, entityId) {
        try {
            const result = await apiPatch(`${config.api.paths.newsLinkBlock}/${entityId}/block`, { block_id: parseInt(blockId, 10) });
            if (result?.id) {
                blockElement.setAttribute('data-news-id', result.id);
                blockElement.setAttribute('data-news-status', result.status || 'draft');
                blockElement.setAttribute('data-news-slug', result.slug);
                blockElement.removeAttribute('data-pending-link-id');
                blockElement.removeAttribute('data-pending-link-slug');
                this.activateNewsLink(blockElement, result.slug);
            }
        } catch (err) {
            console.error('Error applying pending news link:', err);
        }
    }

    // ВАЖНО: Делаем метод публичным, убрав async (он не использует await)
    activateNewsLink(blockElement, blockName) {
        const newsLink = blockElement.querySelector('a.news-link');
        if (newsLink) {
            const dynamicUrl = blockName && blockName.startsWith('case-') ? `/case/${blockName}` : `/news/${blockName}`;
            newsLink.href = dynamicUrl;
            newsLink.setAttribute('data-page', blockName);
            newsLink.classList.remove('inactive');
            newsLink.classList.add('active');

            console.log(`News link activated for ${blockName}: ${dynamicUrl}`);
        }
    }

    async createNewsEntity(blockElement, blockId, blockData) {
        const token = getAuthToken();
        if (!token) return;

        try {
            const blockName = blockData.data_block_name;

            // Создаем slug для новости из имени блока
            const newsSlug = blockName;

            const newsData = {
                title: blockName.replace('news-block-', '').replace(/-/g, ' ').toUpperCase(),
                slug: newsSlug,
                preview_text: 'Add preview text in content editor',
                preview_image: null,
                page_title: `News - ${blockName}`,
                page_slug: `news-${Date.now()}`,
                block_data_name: blockName  // Только имя блока
                // Убираем block_class_name
            };

            console.log('Creating news entity with data:', newsData);

            let result = await apiPost('/api/news', newsData);
            if (!result?.id) {
                const existing = await apiGet(`/api/news/slug/${newsSlug}`, { auth: false }).catch(() => null);
                if (existing?.id && blockId) {
                    result = await apiPatch(
                        `${config.api.paths.newsLinkBlock}/${existing.id}/block`,
                        { block_id: parseInt(blockId, 10) }
                    ).catch(() => null);
                }
            }

            if (result && result.id) {
                // Update block element with news info
                blockElement.setAttribute('data-news-id', result.id);
                blockElement.setAttribute('data-news-status', result.status || 'draft');
                blockElement.setAttribute('data-news-slug', result.slug);

                // Add status indicator - ТОЛЬКО на news-feed
                if (this.shouldShowStatusControls()) {
                    this.updateBlockStatusIndicator(blockElement, result.status || 'draft');
                }

                // Add status controls for authenticated users - ТОЛЬКО на news-feed
                if (getAuthToken() && this.shouldShowStatusControls()) {
                    this.addStatusControlsToBlock(blockElement, result.id);
                }

                this.addLinkButtonToBlock(blockElement);
                console.log('News entity created successfully:', result);
                console.log(`Block ${blockName} now linked to news ID: ${result.id}`);
                showSuccessNotification('News article created in draft status');
            }

        } catch (error) {
            console.error('Error creating news entity:', error);
            showErrorNotification('Failed to create news article');
        }
    }

    // ВАЖНО: Делаем метод публичным для использования из block-renderer
    addStatusControlsToBlock(blockElement, newsId) {
        // НОВОЕ: Проверяем, должны ли показываться контролы
        if (!this.shouldShowStatusControls()) {
            return;
        }

        // Удаляем существующие контролы если есть
        const existingControls = blockElement.querySelector('.news-status-controls');
        if (existingControls) {
            existingControls.remove();
        }

        const currentStatus = blockElement.getAttribute('data-news-status') || 'draft';

        const controlsContainer = document.createElement('div');
        controlsContainer.className = 'news-status-controls';
        controlsContainer.innerHTML = `
      <div class="status-controls-inner">
        <span class="current-status">Status: ${currentStatus.toUpperCase()}</span>
        <button class="toggle-status-btn" data-news-id="${newsId}" data-current-status="${currentStatus}">
          ${currentStatus === 'draft' ? 'Publish' : 'Unpublish'}
        </button>
      </div>
    `;

        blockElement.appendChild(controlsContainer);

        // Add event listener for status toggle
        const toggleBtn = controlsContainer.querySelector('.toggle-status-btn');
        toggleBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggleNewsStatus(blockElement, newsId, currentStatus);
        });

        this._addSlugEditor(blockElement, newsId, 'news');
        console.log(`Status controls added to block for news ID: ${newsId}`);
    }

    async toggleNewsStatus(blockElement, newsId, currentStatus) {
        const token = getAuthToken();
        if (!token) {
            showErrorNotification('Authentication required');
            return;
        }

        try {
            const newStatus = currentStatus === 'draft' ? 'published' : 'draft';

            console.log(`Toggling news status from ${currentStatus} to ${newStatus}`);

            // Call the toggle publication endpoint
            const result = await apiPost(`/api/news/${newsId}/publish`);

            if (result) {
                // Update block attributes
                blockElement.setAttribute('data-news-status', result.status);

                // Update status indicator
                this.updateBlockStatusIndicator(blockElement, result.status);

                // Update controls
                this.updateStatusControls(blockElement, result.status);

                const statusText = result.status === 'published' ? 'published' : 'unpublished';
                showSuccessNotification(`News article ${statusText} successfully`);

                console.log('Status updated successfully:', result);
            }

        } catch (error) {
            console.error('Error toggling news status:', error);
            showErrorNotification('Failed to update news status');
        }
    }

    // ВАЖНО: Делаем метод публичным для использования из block-renderer
    updateBlockStatusIndicator(blockElement, status) {
        // НОВОЕ: Проверяем, должны ли показываться индикаторы
        if (!this.shouldShowStatusControls()) {
            return;
        }

        let indicator = blockElement.querySelector('.news-status-indicator');

        if (!indicator) {
            indicator = document.createElement('div');
            indicator.className = 'news-status-indicator';
            blockElement.appendChild(indicator);
        }

        indicator.className = `news-status-indicator status-${status}`;
        indicator.innerHTML = `<span class="status-text">${status.toUpperCase()}</span>`;
    }

    updateStatusControls(blockElement, status) {
        const controls = blockElement.querySelector('.news-status-controls');
        if (controls) {
            const statusSpan = controls.querySelector('.current-status');
            const toggleBtn = controls.querySelector('.toggle-status-btn');

            if (statusSpan) {
                statusSpan.textContent = `Status: ${status.toUpperCase()}`;
            }

            if (toggleBtn) {
                toggleBtn.textContent = status === 'draft' ? 'Publish' : 'Unpublish';
                toggleBtn.setAttribute('data-current-status', status);
            }
        }
    }

    addLinkButtonToBlock(blockElement) {
        if (!getAuthToken()) return;
        if (blockElement.querySelector('.news-link-btn')) return;

        const btn = document.createElement('button');
        btn.className = 'news-link-btn block-control-btn';
        btn.title = 'Прив\'язати сторінку';
        btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
            <path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1 1 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4 4 0 0 1-.128-1.287z"/>
            <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243z"/>
        </svg>`;
        Object.assign(btn.style, {
            position: 'absolute', top: '10px', right: '70px',
            background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '4px',
            color: 'white', cursor: 'pointer', padding: '4px', zIndex: '10',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        });

        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const blockId = blockElement.getAttribute('data-block-id') || null;
            const currentSlug = blockElement.getAttribute('data-news-slug') || blockElement.getAttribute('data-pending-link-slug') || null;
            openLinkPageModal(blockId, currentSlug, (result) => {
                if (blockId) {
                    if (result.slug) {
                        blockElement.setAttribute('data-news-id', result.id);
                        blockElement.setAttribute('data-news-slug', result.slug);
                        this.activateNewsLink(blockElement, result.slug);
                    } else {
                        blockElement.removeAttribute('data-news-slug');
                        this.activateNewsLink(blockElement, null);
                    }
                } else {
                    blockElement.setAttribute('data-pending-link-id', result.id);
                    blockElement.setAttribute('data-pending-link-slug', result.slug);
                    btn.style.background = 'rgba(25,118,210,0.8)';
                }
            }, {
                listPath: `${config.api.paths.news}?limit=200&exclude_cases=true`,
                linkPath: config.api.paths.newsLinkBlock,
            });
        });

        blockElement.appendChild(btn);
    }

    processExistingNewsBlocks() {
        const newsBlocks = document.querySelectorAll('[data-block-name^="news-"][data-block-id]');

        newsBlocks.forEach(blockElement => {
            const blockName = blockElement.getAttribute('data-block-name');
            const newsId = blockElement.getAttribute('data-news-id');
            const newsStatus = blockElement.getAttribute('data-news-status') || 'draft';
            const newsSlug = blockElement.getAttribute('data-news-slug');

            // Activate link for any published block
            this.activateNewsLink(blockElement, blockName);

            // Add status indicator - ТОЛЬКО на news-feed
            if (this.shouldShowStatusControls()) {
                this.updateBlockStatusIndicator(blockElement, newsStatus);
            }

            // Add status controls if news entity exists and user is authenticated - ТОЛЬКО на news-feed
            if (newsId && getAuthToken() && this.shouldShowStatusControls()) {
                this.addStatusControlsToBlock(blockElement, newsId);
            }

            this.addLinkButtonToBlock(blockElement);
        });
    }

    // Static method to handle news block deletion - called by block system
    static async handleNewsBlockDeletion(blockElement) {
        const newsId = blockElement.getAttribute('data-news-id');
        const blockName = blockElement.getAttribute('data-block-name');

        if (!newsId) {
            console.log(`News block ${blockName} has no news entity to delete`);
            return false; // No news entity to delete
        }

        if (!getAuthToken()) {
            console.error('Authentication required for news deletion');
            return false;
        }

        try {
            console.log(`Deleting news entity: ${newsId} for block: ${blockName}`);

            // Call the cascading delete endpoint
            await apiDelete(`/api/news/${newsId}`);

            console.log(`News entity ${newsId} deleted successfully with cascade`);
            return true; // Successfully deleted via news endpoint

        } catch (error) {
            console.error('Error deleting news entity:', error);
            return false; // Failed to delete via news endpoint
        }
    }

    // Method for handling dynamic news page
    setupDynamicNewsPage() {
        const urlParams = new URLSearchParams(window.location.search);
        const newsSlug = urlParams.get('slug');

        if (newsSlug) {
            this.applyDynamicContent(newsSlug);
        }
    }

    applyDynamicContent(newsSlug) {
        // Update data-page
        document.body.setAttribute('data-page', newsSlug);

        // Update dynamic elements
        const elements = [
            {id: 'dynamic-date', suffix: 'date'},
            {id: 'dynamic-title', suffix: 'title'},
            {id: 'dynamic-description', suffix: 'description'}
        ];

        elements.forEach(({id, suffix}) => {
            const element = document.getElementById(id);
            if (element) {
                element.setAttribute('data-content', `${newsSlug}-${suffix}`);
                element.setAttribute('data-global', 'true');
            }
        });

        console.log('Dynamic content applied for news slug:', newsSlug);
    }

    _toSlug(text) {
        const uk = {
            'а':'a','б':'b','в':'v','г':'h','ґ':'g','д':'d','е':'e','є':'ye',
            'ж':'zh','з':'z','и':'y','і':'i','ї':'yi','й':'y','к':'k','л':'l',
            'м':'m','н':'n','о':'o','п':'p','р':'r','с':'s','т':'t','у':'u',
            'ф':'f','х':'kh','ц':'ts','ч':'ch','ш':'sh','щ':'shch','ь':'',
            'ю':'yu','я':'ya',
        };
        return text.toLowerCase()
            .split('').map(c => uk[c] ?? c).join('')
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_]+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    }

    _addSlugEditor(blockElement, entityId, type) {
        if (blockElement.querySelector('.slug-editor')) return;

        const currentSlug = blockElement.getAttribute(`data-${type}-slug`) || '';
        const apiPath = type === 'news' ? `/api/news/${entityId}` : `/api/vacancies/${entityId}`;

        const wrap = document.createElement('div');
        wrap.className = 'slug-editor';
        Object.assign(wrap.style, {
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '4px 6px', background: 'rgba(0,0,0,0.55)',
            borderRadius: '0 0 4px 4px', flexWrap: 'wrap',
        });
        wrap.innerHTML = `
            <span style="color:#aaa;font-size:11px;white-space:nowrap">slug:</span>
            <input class="slug-input" type="text" value="${currentSlug}"
                style="flex:1;min-width:80px;font-size:11px;padding:2px 4px;border-radius:3px;border:1px solid #555;background:#222;color:#fff;outline:none">
            <button class="slug-auto-btn" title="Авто з заголовку"
                style="font-size:10px;padding:2px 5px;background:#444;border:none;border-radius:3px;color:#ccc;cursor:pointer;white-space:nowrap">Auto</button>
            <button class="slug-save-btn"
                style="font-size:10px;padding:2px 6px;background:#1976d2;border:none;border-radius:3px;color:#fff;cursor:pointer;white-space:nowrap">Save</button>
        `;

        const input = wrap.querySelector('.slug-input');
        const autoBtn = wrap.querySelector('.slug-auto-btn');
        const saveBtn = wrap.querySelector('.slug-save-btn');

        autoBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const titleEl = blockElement.querySelector('h2, h3, h4, .news-feed-title, .vacancy-name');
            if (titleEl) input.value = this._toSlug(titleEl.textContent.trim());
        });

        saveBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const newSlug = input.value.trim();
            if (!newSlug || newSlug === currentSlug) return;
            try {
                saveBtn.textContent = '...';
                const result = await apiPut(apiPath, { slug: newSlug });
                if (result?.slug) {
                    blockElement.setAttribute(`data-${type}-slug`, result.slug);
                    input.value = result.slug;
                    // Update the clickable link href
                    const link = blockElement.querySelector('a.news-link-to-feed-container, a.active');
                    if (link && link.href) {
                        const prefix = type === 'news' ? '/news/' : '/vacancy/';
                        link.href = prefix + result.slug;
                    }
                    saveBtn.textContent = '✓';
                    setTimeout(() => { saveBtn.textContent = 'Save'; }, 1500);
                    showSuccessNotification(`Slug оновлено: ${result.slug}`);
                }
            } catch (err) {
                saveBtn.textContent = 'Save';
                showErrorNotification('Помилка збереження slug');
            }
        });

        // Stop clicks on the editor from propagating to the block
        wrap.addEventListener('click', e => e.stopPropagation());
        input.addEventListener('keydown', e => e.stopPropagation());

        const controls = blockElement.querySelector('.news-status-controls, .vacancy-status-controls');
        if (controls) controls.after(wrap);
        else blockElement.appendChild(wrap);
    }
}

export const newsManager = new NewsManager();

// ВАЖНО: Делаем newsManager доступным глобально
window.newsManager = newsManager;

console.log('NewsManager initialized and made globally available');